<template>
  <v-dialog
    v-model="dialog"
    width="50%"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-form ref="form" v-model="valid" @submit.prevent="submit()">
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <alert-message></alert-message>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-text-field
                  v-model="form.name"
                  label="Name"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.name'),
                      serverSideErrors('Name')
                    )
                  "
                  @blur="$v.form.name.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <client-select
                  :error-messages="
                    [].concat(
                      fieldErrors('form.clientId'),
                      serverSideErrors('ClientId')
                    )
                  "
                  v-model="form.clientId"
                  label="Select Client"
                  @blur="$v.form.clientId.$touch()"
                />
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <date-picker
                  v-model="form.date"
                  label="Date"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.date'),
                      serverSideErrors('Date')
                    )
                  "
                  @blur="$v.form.date.$touch()"
                />
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <v-text-field
                  type="number"
                  v-model="form.payRate"
                  label="Pay Rate"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.payRate'),
                      serverSideErrors('PayRate')
                    )
                  "
                  @blur="$v.form.payRate.$touch()"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <user-select
                  v-model="form.userIds"
                  label="Assign To"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.userIds'),
                      serverSideErrors('UserIds')
                    )
                  "
                  @blur="$v.form.userIds.$touch()"
                />
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <v-select
                  v-model="form.type"
                  :items="typeOptions"
                  item-text="text"
                  item-value="value"
                  label="Type"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.type'),
                      serverSideErrors('Type')
                    )
                  "
                  @blur="$v.form.type.$touch()"
                />
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <v-select
                  v-model="form.status"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="Select Status"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.status'),
                      serverSideErrors('Status')
                    )
                  "
                  @blur="$v.form.task.status.$touch()"
                  persistent-hint
                  single-line
                ></v-select>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <v-textarea
                  v-model="form.description"
                  label="Description"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.description'),
                      serverSideErrors('Description')
                    )
                  "
                  @blur="$v.form.description.$touch()"
                  rows="1"
                  auto-grow
                  required
                ></v-textarea>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <v-textarea
                  v-model="form.internalNote"
                  label="Internal Note"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.internalNote'),
                      serverSideErrors('InternalNote')
                    )
                  "
                  @blur="$v.form.internalNote.$touch()"
                  rows="1"
                  auto-grow
                  required
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="darken-1" @click="dialog = false">Cancel</v-btn>
          <v-btn
            :disabled="$v.$invalid"
            type="submit"
            color="info"
            :loading="loading"
          >
            Create Time Clock
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import UTIL from "src/helpers/utility";
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import AlertMessage from "src/components/AlertMessage";
import { required } from "vuelidate/lib/validators";
import validationMixin from "@/mixins/validationMixin";
import ClientSelect from "src/components/ClientSelect.vue";
import DatePicker from "src/components/DatePicker.vue";
import UserSelect from "src/components/UserSelect";
import moment from "moment";

const defaultInputs = {
  id: "",
  name: "",
  status: "open",
  type: "Labor",
  date: moment().format("yyyy-MM-DD"),
  description: "",
  clientId: "",
  payRate: 0,
  internalNote: "",
  userIds: [],
};

export default {
  components: {
    AlertMessage,
    ClientSelect,
    DatePicker,
    UserSelect,
  },
  props: {
    clientId: Number,
    item: Object,
    show: [Boolean, String],
  },
  mixins: [validationMixin],
  validations: {
    form: {
      userIds: { required },
      name: {
        required,
      },
      status: { required },
      type: { required },
      date: { required },
      clientId: { required },
      description: {},
      payRate: {},
      internalNote: {},
    },
  },
  validationMessages: {
    form: {
      userIds: { required: "Required" },
      name: {
        required: "Required",
      },
      status: {
        required: "Required",
      },
      type: {
        required: "Required",
      },
      date: {
        required: "Required",
      },
      description: {
        required: "Required",
      },
      clientId: {
        required: "Required",
      },
      payRate: {},
      internalNote: {},
    },
  },

  data() {
    return {
      showDialog: this.show,

      form: {
        ...defaultInputs,
        clientId: this.clientId,
      },

      typeOptions: [
        { value: "remote", text: "Remote Support" },
        { value: "onsite", text: "Onsite Support" },
      ],

      statusOptions: [
        { value: "open", text: "Open" },
        { value: "closed", text: "Closed" },
      ],

      dialog: false,
      valid: false,
      loading: false,
    };
  },

  watch: {
    show(n, o) {
      if (n && !o) {
        this.dialog = true;
      }
    },

    dialog(n, o) {
      if (!n && o) {
        this.onDialogClose();
      } else {
        this.onDialogOpen();
      }
    },

    item(n, o) {
      if (n && JSON.stringify(n) !== JSON.stringify(o)) {
        let userIds = [];
        if (n.taskUsers) {
          userIds = n.taskUsers.map((user) => user.userId);
        }
        this.form = {
          ..._.pick(n, _.keys(defaultInputs)),
          userIds,
        };
      }
    },
  },

  computed: {
    ...mapState({
      account: (state) => state.account,
      task: (state) => state.task,
      alert: (state) => state.alert,
    }),
    formTitle() {
      return "Convert To Time Clock";
    },
    isEdit() {
      return UTIL.gv(this.item, "id", "") ? true : false;
    },
    taskId() {
      return UTIL.gv(this.item, "id", "");
    },
  },

  methods: {
    ...mapActions("timeclock", ["createFromTask"]),

    initForm() {
      if (this.clientId) {
        this.form = {
          ...this.form.task,
          clientId: this.clientId,
        };
      }
    },

    serverSideErrors(field) {
      return UTIL.gv(this.task.errors, field, []);
    },
    onDialogClose() {
      if (this.isEdit) {
        //
      } else {
        //
      }
      this.$emit("onClosed");
    },

    onDialogOpen() {
      this.$nextTick(() => {
        // clear error state
        this.$store.dispatch("alert/clear");
        this.$v.$reset();
      });

      this.initForm();
    },

    save() {},
    addNew() {
      this.dialog = true;
      this.form = {
        ...defaultInputs,
      };
    },
    completed() {
      this.$emit("onSucceded");
    },
    async submit() {
      this.loading = true;
      if (!this.valid) {
        this.$store.dispatch("snackbar/error", "Form invalid");
        return false;
      }

      let payload = {
        ...this.form,
        id: this.item.id,
      };
      await this.createFromTask(payload);

      if (!this.task.status.error) {
        this.dialog = false;
        this.completed();
      }
      this.loading = false;
    },
  },
};
</script>